import type { BacktestMetrics, Candle } from '../../domain/types'
import { DEFAULT_COSTS, generateSignals, simulateSignals, type CostModel, type StockStrategyParams, type StockStrategyType } from './backtest'
import {
  bestOf,
  evaluateGrid,
  gridSegmentMatrix,
  runMetrics,
  trainTestSplit,
  walkForwardOptimize,
  type GridEntry,
  type Objective,
  type WalkForwardOptimizationResult,
} from './optimize'
import { parameterSensitivity, type SensitivitySummary } from './sensitivity'
import {
  bootstrapReturns,
  permutationDrawdown,
  probabilityOfBacktestOverfitting,
  type BootstrapSummary,
  type PboResult,
  type PermutationDrawdown,
} from './resampling'
import { clamp } from './statistics'

export interface CostSensitivity {
  baseReturnPct: number
  doubleReturnPct: number
  tripleReturnPct: number
  survivalPct: number
  survivesDoubleCosts: boolean
}

export function costSensitivity(candles: readonly Candle[], params: StockStrategyParams, baseCosts: CostModel = DEFAULT_COSTS): CostSensitivity {
  const scaled = (k: number): CostModel => ({ feePct: baseCosts.feePct * k, slippagePct: baseCosts.slippagePct * k, spreadPct: baseCosts.spreadPct * k })
  const base = runMetrics(candles, params, baseCosts).totalReturnPct
  const dbl = runMetrics(candles, params, scaled(2)).totalReturnPct
  const tpl = runMetrics(candles, params, scaled(3)).totalReturnPct
  const survivalPct = base > 1e-9 ? clamp((dbl / base) * 100, -100, 200) : dbl > 0 ? 100 : 0
  return { baseReturnPct: base, doubleReturnPct: dbl, tripleReturnPct: tpl, survivalPct, survivesDoubleCosts: dbl > 0 }
}

export interface EraResult {
  label: string
  returnPct: number
  maxDrawdownPct: number
  trades: number
  profitable: boolean
}

export interface EraReplay {
  eras: EraResult[]
  profitableEras: number
  regimeDependent: boolean
  verdict: string
}

// "Would this have worked before?" — run the SAME params on each chronological era.
export function historicalReplay(candles: readonly Candle[], params: StockStrategyParams, costs: CostModel = DEFAULT_COSTS, nEras = 4): EraReplay {
  const size = Math.floor(candles.length / nEras)
  const eras: EraResult[] = []
  if (size >= 15) {
    for (let e = 0; e < nEras; e++) {
      const start = e * size
      const end = e === nEras - 1 ? candles.length : start + size
      const m = runMetrics(candles.slice(start, end), params, costs)
      eras.push({ label: `Era ${e + 1}`, returnPct: m.totalReturnPct, maxDrawdownPct: m.maxDrawdownPct, trades: m.trades, profitable: m.totalReturnPct > 0 })
    }
  }
  const profitableEras = eras.filter((e) => e.profitable).length
  const regimeDependent = eras.length >= 3 && profitableEras <= 1
  const verdict =
    eras.length === 0
      ? 'Not enough history to replay across eras.'
      : regimeDependent
        ? 'Regime-dependent: it only worked in one slice of history. The edge may not generalize.'
        : profitableEras === eras.length
          ? 'Worked across every era tested — encouraging, though no guarantee of the future.'
          : `Mixed: profitable in ${profitableEras} of ${eras.length} eras. Expect long flat or losing stretches.`
  return { eras, profitableEras, regimeDependent, verdict }
}

export interface OverfitInput {
  trades: number
  isReturnPct: number
  oosReturnPct: number
  wfEfficiency: number
  wfConsistency: number
  fragilityScore: number
  bootstrapPProfit: number
  costSurvivalPct: number
  pbo: number
}

export type OverfitLevel = 'low' | 'elevated' | 'high' | 'severe'

export interface OverfitAssessment {
  score: number
  level: OverfitLevel
  reasons: string[]
  signals: OverfitInput
}

// Composite 0-100 overfit risk. Higher = more likely the backtest is curve-fit / lucky / unrealistic.
export function assessOverfit(input: OverfitInput): OverfitAssessment {
  const sampleRisk = clamp((30 - input.trades) / 30, 0, 1)
  const degradationRisk = clamp(1 - input.wfEfficiency, 0, 1)
  const consistencyRisk = clamp(1 - input.wfConsistency, 0, 1)
  const fragilityRisk = clamp(input.fragilityScore, 0, 1)
  const luckRisk = clamp(1 - input.bootstrapPProfit, 0, 1)
  const costRisk = input.costSurvivalPct >= 0 ? clamp(1 - input.costSurvivalPct / 100, 0, 1) : 1
  const pboRisk = clamp(input.pbo, 0, 1)

  const score = clamp(
    100 *
      (0.18 * sampleRisk +
        0.22 * degradationRisk +
        0.15 * consistencyRisk +
        0.15 * fragilityRisk +
        0.1 * luckRisk +
        0.08 * costRisk +
        0.12 * pboRisk),
    0,
    100,
  )

  const reasons: string[] = []
  if (input.trades < 10) reasons.push(`Only ${input.trades} trades — far too few for any metric to be trustworthy. This is noise.`)
  if (input.isReturnPct > 0 && input.oosReturnPct <= 0) reasons.push('Profitable in-sample but flat-to-negative out-of-sample — the textbook overfit signature.')
  if (input.wfEfficiency < 0.5) reasons.push(`Walk-forward efficiency ${(input.wfEfficiency * 100).toFixed(0)}% — most of the in-sample edge vanishes out-of-sample.`)
  if (input.wfConsistency < 0.5) reasons.push(`Positive in only ${(input.wfConsistency * 100).toFixed(0)}% of walk-forward folds — inconsistent.`)
  if (input.fragilityScore > 0.5) reasons.push('Performance is fragile to parameter changes — a narrow, suspicious peak.')
  if (input.bootstrapPProfit < 0.6) reasons.push(`Bootstrap shows only a ${(input.bootstrapPProfit * 100).toFixed(0)}% chance the edge is real rather than luck.`)
  if (input.costSurvivalPct < 50 && input.costSurvivalPct >= 0) reasons.push('Most of the edge disappears when costs are doubled — it is not robust to realistic frictions.')
  if (input.pbo > 0.5) reasons.push(`Probability of backtest overfitting is ${(input.pbo * 100).toFixed(0)}% — the chosen config is likely a false discovery.`)
  if (reasons.length === 0) reasons.push('No single red flag dominates, but treat every backtest with healthy suspicion.')

  let level: OverfitLevel = 'low'
  if (score >= 75) level = 'severe'
  else if (score >= 50) level = 'high'
  else if (score >= 25) level = 'elevated'

  return { score, level, reasons, signals: input }
}

export interface RedTeamInput {
  name: string
  trades: number
  isReturnPct: number
  oosReturnPct: number
  nConfigs: number
  fragilityScore: number
  costSurvivalPct: number
  pbo: number
  regimeDependent: boolean
  bootstrapPProfit: number
  overfitScore: number
}

export function redTeamStrategy(input: RedTeamInput): string[] {
  const args: string[] = []
  args.push(`You tried ${input.nConfigs} parameter sets and kept the best. With that many shots, a great-looking result is expected even from pure noise (multiple-testing bias).`)
  if (input.trades < 20) args.push(`${input.trades} trades is a tiny sample. One or two outliers could be the entire "edge."`)
  if (input.isReturnPct > 0 && input.oosReturnPct < input.isReturnPct * 0.5) args.push(`Out-of-sample return (${input.oosReturnPct.toFixed(1)}%) is far below in-sample (${input.isReturnPct.toFixed(1)}%). What changed — the market, or your luck?`)
  if (input.fragilityScore > 0.45) args.push('Nudge the parameters slightly and the profit evaporates. A real edge should not balance on a knife-edge.')
  if (input.costSurvivalPct < 60 && input.costSurvivalPct >= 0) args.push('Double the trading costs and the edge largely disappears. Are your assumed fees, spread, and slippage realistic?')
  if (input.pbo > 0.4) args.push(`A ${(input.pbo * 100).toFixed(0)}% probability of backtest overfitting says this "best" config is more likely a false positive than a genuine discovery.`)
  if (input.regimeDependent) args.push('It only worked in one historical regime. Markets change — why will the next regime resemble that one?')
  if (input.bootstrapPProfit < 0.7) args.push(`Resampling gives only ${(input.bootstrapPProfit * 100).toFixed(0)}% confidence of profit. Would you bet real money on a coin this close to fair?`)
  args.push('What is the economic mechanism? If you cannot explain WHY this edge exists, assume it is data-mined.')
  args.push('Is there any look-ahead leakage, survivorship bias, or unrealistic fill assumption hiding in the data?')
  return args
}

export interface StrategyEvalBundle {
  bestParams: StockStrategyParams
  nConfigs: number
  grid: GridEntry[]
  metrics: BacktestMetrics
  train: BacktestMetrics
  test: BacktestMetrics
  degradationPct: number
  walkForward: WalkForwardOptimizationResult
  sensitivity: SensitivitySummary
  bootstrap: BootstrapSummary
  permutation: PermutationDrawdown
  pbo: PboResult
  eraReplay: EraReplay
  costSensitivity: CostSensitivity
  overfit: OverfitAssessment
  redTeam: string[]
  equityCurve: number[]
}

// Full evaluation pipeline: optimize, train/test, walk-forward, sensitivity, resampling, PBO, eras, costs, red-team.
export function evaluateStrategy(
  candles: readonly Candle[],
  type: StockStrategyType,
  costs: CostModel = DEFAULT_COSTS,
  objective: Objective = 'return',
): StrategyEvalBundle {
  const grid = evaluateGrid(candles, type, costs, objective)
  const best = bestOf(grid)
  const sim = simulateSignals(candles, generateSignals(candles, best.params), costs)
  const tt = trainTestSplit(candles, type, costs, objective)
  const wfo = walkForwardOptimize(candles, type, costs, objective, 4)
  const sensitivity = parameterSensitivity(grid, type)
  const seg = gridSegmentMatrix(candles, type, 8, costs, objective)
  const pbo = probabilityOfBacktestOverfitting(seg.matrix)
  const bootstrap = bootstrapReturns(sim.stepReturns)
  const permutation = permutationDrawdown(sim.stepReturns)
  const eraReplay = historicalReplay(candles, best.params, costs, 4)
  const cs = costSensitivity(candles, best.params, costs)

  const overfit = assessOverfit({
    trades: best.metrics.trades,
    isReturnPct: tt.train.totalReturnPct,
    oosReturnPct: wfo.oosReturnPct,
    wfEfficiency: wfo.efficiency,
    wfConsistency: wfo.consistency,
    fragilityScore: sensitivity.fragilityScore,
    bootstrapPProfit: bootstrap.pProfit,
    costSurvivalPct: cs.survivalPct,
    pbo: pbo.pbo,
  })

  const redTeam = redTeamStrategy({
    name: type,
    trades: best.metrics.trades,
    isReturnPct: tt.train.totalReturnPct,
    oosReturnPct: wfo.oosReturnPct,
    nConfigs: grid.length,
    fragilityScore: sensitivity.fragilityScore,
    costSurvivalPct: cs.survivalPct,
    pbo: pbo.pbo,
    regimeDependent: eraReplay.regimeDependent,
    bootstrapPProfit: bootstrap.pProfit,
    overfitScore: overfit.score,
  })

  return {
    bestParams: best.params,
    nConfigs: grid.length,
    grid,
    metrics: best.metrics,
    train: tt.train,
    test: tt.test,
    degradationPct: tt.degradationPct,
    walkForward: wfo,
    sensitivity,
    bootstrap,
    permutation,
    pbo,
    eraReplay,
    costSensitivity: cs,
    overfit,
    redTeam,
    equityCurve: sim.equityCurve,
  }
}
